'use client';

/**
 * SysObeyaCohesionModulesTableCard — per-module cohesion breakdown.
 *
 * Slices `response.data[0].modules[]` from the system_obeya_view
 * cohesion composite call. Sortable; identifies which modules carry
 * the procedural-pattern debt. Columns: module, flagged count,
 * high-procedural count, avg score, total classes (denominator).
 *
 * Typography from `useTypographyBundle('canvas_dashboard')` — same
 * pattern as `high-cc-modules-table-card.tsx`. Container chrome lives
 * here as render concerns per the mode7 doctrine. Chrome strings come
 * from `response.labels`.
 *
 * @see klyde_edge/ui_config/composition/cards/system_obeya.py — content_key="sys_obeya_cohesion_modules_table"
 * @see klyde_edge/ui_config/labels/system_obeya.py — cohesion_modules_table_*, cohesion_col_*
 */

import { useState, useMemo } from 'react';
import { Layers, ChevronDown, ChevronUp } from 'lucide-react';
import { cn } from '@/lib/utils/cn';
import { type TypographyBundleSlots } from '@/lib/design/typography';
import { useCardTypography } from '@/lib/design/card-chrome';
import { useCardExport } from '@/lib/design/card-export';
import { useSystemObeyaView, type SystemObeyaLabels } from './use-system-obeya-view';

interface Props {
  view_name?: string;
}

type SortDir = 'asc' | 'desc';
type Align = 'left' | 'right';

interface ColumnDef {
  key: string;
  label_key: string;
  align: Align;
}

const COLUMNS: ColumnDef[] = [
  { key: 'package',               label_key: 'cohesion_col_module',    align: 'left'  },
  { key: 'flagged_count',         label_key: 'cohesion_col_flagged',   align: 'right' },
  { key: 'high_procedural_count', label_key: 'cohesion_col_high',      align: 'right' },
  { key: 'avg_procedural_score',  label_key: 'cohesion_col_avg_score', align: 'right' },
];

export function SysObeyaCohesionModulesTableCard({ view_name = 'cohesion' }: Props) {
  const { data, isLoading, queryKey } = useSystemObeyaView(view_name);
  const T = useCardTypography();
  const L: SystemObeyaLabels = data?.labels ?? {};

  const [sortKey, setSortKey] = useState<string>('high_procedural_count');
  const [sortDir, setSortDir] = useState<SortDir>('desc');

  const rows = useMemo<Record<string, unknown>[]>(() => {
    const first = (data?.data ?? [])[0] as { modules?: Record<string, unknown>[] } | undefined;
    const modules = first?.modules ?? [];
    return [...modules].sort((a, b) => {
      const av = a[sortKey];
      const bv = b[sortKey];
      if (typeof av === 'number' && typeof bv === 'number') {
        return sortDir === 'asc' ? av - bv : bv - av;
      }
      const as = String(av ?? '');
      const bs = String(bv ?? '');
      return sortDir === 'asc' ? as.localeCompare(bs) : bs.localeCompare(as);
    });
  }, [data, sortKey, sortDir]);

  // Card export — markdown table of the sorted module breakdown.
  const title = L.cohesion_modules_table_title ?? '';
  useCardExport(useMemo(() => {
    const headers = COLUMNS.map((c) => L[c.label_key] ?? c.key);
    const md = [
      `| ${headers.join(' | ')} |`,
      `| ${headers.map(() => '---').join(' | ')} |`,
      ...rows.map((row) =>
        `| ${COLUMNS.map((c) => formatCellForExport(c, row, L)).join(' | ')} |`,
      ),
    ].join('\n');
    // Scene json mirrors painted cell strings — formatter's OUTPUT.
    // Raw numerics travel in the capture envelope's payloads section.
    const visibleRows = rows.map((r) =>
      Object.fromEntries(COLUMNS.map((c) => [c.key, formatCellForExport(c, r, L)]))
    );
    return {
      label: title,
      text: rows.map((r) =>
        COLUMNS.map((c) => `${L[c.label_key] ?? c.key}=${formatCellForExport(c, r, L)}`).join(', ')
      ).join('\n'),
      markdown: md,
      json: { columns: COLUMNS.map((c) => c.key), rows: visibleRows },
      content_key: 'sys_obeya_cohesion_modules_table',
      surface: 'canvas',
      state: { sort_key: sortKey, sort_dir: sortDir },
      // Status = paintable content, not the query flag.
      status: (data ? 'loaded' : 'loading') as 'loading' | 'loaded',
      query_key: queryKey,
    };
  }, [rows, L, title, sortKey, sortDir, data, isLoading, queryKey]));

  if (isLoading || !data) {
    return (
      <CardShell>
        <div className="p-6">
          <div className={T.body}>{L.loading_message ?? ''}</div>
        </div>
      </CardShell>
    );
  }

  return (
    <CardShell>
      <div className="p-6 border-b border-border/50">
        <div className="flex items-center gap-2">
          <Layers className="w-5 h-5 text-primary" />
          <h2 className={T.header}>{L.cohesion_modules_table_title ?? ''}</h2>
        </div>
      </div>
      <div className="overflow-auto flex-1">
        <table className="w-full">
          <thead className="sticky top-0 bg-card">
            <tr className="border-b border-border/50">
              {COLUMNS.map((col) => (
                <th
                  key={col.key}
                  onClick={() => {
                    if (sortKey === col.key) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
                    else { setSortKey(col.key); setSortDir('desc'); }
                  }}
                  className={cn(
                    T.metadata,
                    'uppercase tracking-wider font-semibold px-3 py-3 cursor-pointer hover:text-primary select-none',
                    col.align === 'right' ? 'text-right' : 'text-left',
                  )}
                >
                  <span className="inline-flex items-center gap-1">
                    {L[col.label_key] ?? ''}
                    {sortKey === col.key && (sortDir === 'asc' ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />)}
                  </span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, idx) => {
              // Three data-shape states, one row treatment each:
              // analysed-with-findings + analysed-clean render normally;
              // not-analysed (cohesion stage absent — discovery) dims so
              // "no data" never masquerades as "zero findings".
              const notAnalysed = row.cohesion_analysed === false;
              return (
                <tr
                  key={(row.package as string) ?? `row-${idx}`}
                  className={cn(
                    'border-b border-border/40 hover:bg-muted/30 transition-colors',
                    notAnalysed && 'opacity-55',
                  )}
                >
                  {COLUMNS.map((col) => (
                    <Cell key={col.key} col={col} row={row} T={T} L={L} />
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </CardShell>
  );
}

function Cell({
  col,
  row,
  T,
  L,
}: {
  col: ColumnDef;
  row: Record<string, unknown>;
  T: TypographyBundleSlots;
  L: SystemObeyaLabels;
}) {
  const dash = L.dash_placeholder ?? '—';

  if (col.key === 'package') {
    const moduleName = (row.display_name as string) || (row.package as string) || '';
    const notAnalysed = row.cohesion_analysed === false;
    return (
      <td className={cn('px-3 py-2.5', col.align === 'right' ? 'text-right' : 'text-left')}>
        <div className="flex items-center gap-2">
          <span className={cn(T.name, 'font-mono')}>{moduleName || dash}</span>
          {notAnalysed && (
            <span className={cn(T.metadata, 'px-1.5 py-0.5 rounded bg-muted text-muted-foreground font-medium whitespace-nowrap')}>
              {L.cohesion_not_analysed ?? 'not analysed'}
            </span>
          )}
        </div>
      </td>
    );
  }

  return (
    <td className={cn('px-3 py-2.5', col.align === 'right' ? 'text-right' : 'text-left')}>
      <span className={cn(T.body, 'font-mono tabular-nums')}>
        {formatCellForExport(col, row, L)}
      </span>
    </td>
  );
}

function CardShell({ children }: { children: React.ReactNode }) {
  return <div className="rounded-xl border border-border bg-card shadow-sm h-full overflow-hidden flex flex-col">{children}</div>;
}
function fmt(n: unknown): string {
  if (n == null || typeof n !== 'number') return '—';
  return n.toLocaleString();
}

// One formatting owner per cell — the export AND the on-screen Cell
// both route through here so scene == screen by construction.
// `flagged_count` renders as "N of M" when the total_classes
// denominator is present (the legacy report's framing).
function formatCellForExport(col: ColumnDef, row: Record<string, unknown>, L: SystemObeyaLabels): string {
  const dash = L.dash_placeholder ?? '—';
  if (col.key === 'package') {
    return (row.display_name as string) || (row.package as string) || dash;
  }
  // Not-analysed module (cohesion stage absent): the metric cells have
  // no meaning — render dash, never a fake "0 of 0" that reads as clean.
  if (row.cohesion_analysed === false) return dash;
  const value = row[col.key];
  if (value == null) return dash;
  if (col.key === 'flagged_count' && row.total_classes != null) {
    return `${fmt(value)} ${L.cohesion_kpi_flagged_sub_prefix ?? 'of '}${fmt(row.total_classes)}`;
  }
  return fmt(value);
}
